import React, { useEffect, useMemo, useState } from 'react';
import { FaShoppingCart } from 'react-icons/fa';
import databaseService from '../utils/database';

export default function EventsSection({ onAddToCart }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedImages, setExpandedImages] = useState({});

  useEffect(() => {
    let isMounted = true;
    const CACHE_KEY = 'events_cache_v1';
    const MAX_AGE_MS = 5 * 60 * 1000; // 5 minutos

    const loadFromCache = () => {
      try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const { ts, data } = JSON.parse(raw);
        if (!Array.isArray(data)) return null;
        const fresh = Date.now() - (ts || 0) < MAX_AGE_MS;
        return { data, fresh };
      } catch (_) { return null; }
    };

    const saveToCache = (list) => {
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: list }));
      } catch (_) {}
    };

    const fetchEvents = async () => {
      try {
        const resp = await databaseService.getEvents();
        const list = Array.isArray(resp?.events) ? resp.events : (Array.isArray(resp) ? resp : []);
        if (isMounted) {
          setEvents(list);
          setLoading(false);
          saveToCache(list);
        }
      } catch (e) {
        console.error('Error cargando eventos:', e);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Intentar cargar de caché primero para evitar parpadeo
    const cached = loadFromCache();
    if (cached && cached.data) {
      setEvents(cached.data);
      setLoading(false);
      // Si el caché está viejo, refrescar en background
      if (!cached.fresh) {
        fetchEvents();
      }
    } else {
      // No hay caché: fetch normal
      setLoading(true);
      fetchEvents();
    }

    return () => { isMounted = false; };
  }, []);

  const withStatus = useMemo(() => {
    const now = new Date();
    return events.map(ev => {
      const date = new Date(ev.fechaISO || ev.fecha);
      const diffMs = date - now;
      const diffHrs = diffMs / (1000 * 60 * 60);
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      let estado = 'proximo';
      let countdown = '';
      if (diffMs < 0) {
        estado = diffHrs > -6 ? 'en_curso' : 'finalizado';
      } else if (diffHrs < 24) {
        estado = 'proximo';
        const h = Math.ceil(diffHrs);
        countdown = `Faltan ${h} hora${h === 1 ? '' : 's'}`;
      } else {
        const d = Math.ceil(diffDays);
        countdown = `Faltan ${d} día${d === 1 ? '' : 's'}`;
      }
      return { ...ev, _date: date, _estado: estado, _countdown: countdown };
    });
  }, [events]);

  const sorted = useMemo(() => {
    const now = new Date();
    const arr = [...withStatus];
    return arr.sort((a, b) => {
      const rank = (e) => e._estado === 'en_curso' ? 0 : (e._estado === 'proximo' ? 1 : 2);
      const ra = rank(a); const rb = rank(b);
      if (ra !== rb) return ra - rb;
      if (a._date >= now && b._date >= now) return a._date - b._date; // futuros por proximidad
      if (a._date < now && b._date >= now) return 1;
      if (b._date < now && a._date >= now) return -1;
      return b._date - a._date; // pasados: recientes primero
    });
  }, [withStatus]);

  const upcoming = useMemo(() => sorted.filter(e => e._estado !== 'finalizado'), [sorted]);
  const past = useMemo(() => sorted.filter(e => e._estado === 'finalizado'), [sorted]);

  const handleAddToCart = (ev) => {
    if (typeof onAddToCart === 'function') {
      onAddToCart({
        type: 'event_ticket',
        id: ev.id,
        title: ev.titulo,
        price: typeof ev.precio !== 'undefined' && ev.precio !== null ? Number(ev.precio) : 0,
        quantity: 1,
      });
    }
  };

  // Función para obtener emoji según el tipo de evento
  const getTipoEmoji = (tipo) => {
    const tipoUpper = (tipo || '').toUpperCase();
    if (tipoUpper.includes('NETWORKING')) return '🤝';
    if (tipoUpper.includes('BOOTCAMP')) return '💼';
    if (tipoUpper.includes('INTERNATIONAL')) return '🌐';
    if (tipoUpper.includes('EVENTO')) return '🎪';
    return '🏷️'; // Emoji por defecto
  };

  // Función para formatear fecha y hora separadamente
  const formatEventDateTime = (fechaISO) => {
    try {
      const date = new Date(fechaISO);
      if (isNaN(date.getTime())) {
        // Si la fecha no es válida, devolver formato por defecto
        return { fecha: 'Fecha no disponible', hora: 'Hora no disponible' };
      }
      const fecha = date.toLocaleDateString('es-MX', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      });
      const hora = date.toLocaleTimeString('es-MX', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
      return { fecha, hora };
    } catch (error) {
      return { fecha: 'Fecha no disponible', hora: 'Hora no disponible' };
    }
  };

  return (
    <div className="events-section">
      {loading ? (
        <div className="events-grid">
          {[...Array(3)].map((_,i) => (
            <div key={i} className="event-card skeleton">
              <div className="img" />
              <div className="line w-70" />
              <div className="line w-40" />
              <div className="line w-50" />
            </div>
          ))}
        </div>
      ) : (
        <>
        <div className="events-grid">
          {upcoming.length === 0 ? (
            <p>No hay eventos disponibles por ahora.</p>
          ) : upcoming.map(ev => (
            <div key={ev.id} className={`event-card ${ev._estado}`}>
              {ev.imagen_url ? (
                <div 
                  className={`event-image ${expandedImages[ev.id] ? 'expanded' : ''}`}
                  onClick={() => setExpandedImages(prev => ({ ...prev, [ev.id]: !prev[ev.id] }))}
                  title={expandedImages[ev.id] ? 'Colapsar Imagen' : 'Ver Imagen'}
                  aria-label={expandedImages[ev.id] ? 'Colapsar Imagen' : 'Ver Imagen'}
                >
                  <img src={ev.imagen_url} alt={ev.titulo} loading="lazy" />
                </div>
              ) : (
                <div className="event-image placeholder" />
              )}
              <div className="event-body">
                <div className="event-head">
                  <span className={`badge estado ${ev._estado}`}>{ev._estado === 'en_curso' ? 'En curso' : ev._estado === 'proximo' ? 'Próximo' : 'Finalizado'}</span>
                  {ev._countdown && <span className="badge countdown">{ev._countdown}</span>}
                </div>
                <h3 className="title">{ev.titulo}</h3>
                {(() => {
                  const { fecha, hora } = formatEventDateTime(ev.fechaISO || ev.fecha);
                  return (
                    <>
                      <p className="meta"><strong>📅 Fecha:</strong> {fecha}</p>
                      <p className="meta"><strong>⏰ Hora:</strong> {hora}</p>
                    </>
                  );
                })()}
                <p className="meta"><strong>{getTipoEmoji(ev.tipo)} Tipo:</strong> {ev.tipo}</p>
                {ev.lugar && <p className="meta"><strong>📍 Lugar:</strong> {ev.lugar}</p>}
                {typeof ev.precio !== 'undefined' && ev.precio !== null && (
                  <p className="price"><strong>Precio:</strong> <span className="price-amount">${Number(ev.precio).toFixed(2)} MXN</span></p>
                )}
                {ev.descripcion && <p className="desc">{ev.descripcion}</p>}
                <div className="actions">
                  <button 
                    disabled={ev._estado === 'finalizado'}
                    onClick={() => handleAddToCart(ev)}
                    className="cta-buy-btn"
                  >
                    {ev._estado === 'finalizado' ? 'No disponible' : (<><FaShoppingCart /> <span>Comprar Pase</span></>)}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        {past.length > 0 && (
          <>
            <div className="content-header" style={{ marginTop: '24px' }}>
              <h2>Eventos Pasados:</h2>
            </div>
            <div className="events-grid">
              {past.map(ev => (
                <div key={ev.id} className={`event-card ${ev._estado}`}>
                  {ev.imagen_url ? (
                    <div 
                      className={`event-image ${expandedImages[ev.id] ? 'expanded' : ''}`}
                      onClick={() => setExpandedImages(prev => ({ ...prev, [ev.id]: !prev[ev.id] }))}
                      title={expandedImages[ev.id] ? 'Colapsar Imagen' : 'Ver Imagen'}
                      aria-label={expandedImages[ev.id] ? 'Colapsar Imagen' : 'Ver Imagen'}
                    >
                      <img src={ev.imagen_url} alt={ev.titulo} loading="lazy" />
                    </div>
                  ) : (
                    <div className="event-image placeholder" />
                  )}
                  <div className="event-body">
                    <div className="event-head">
                      <span className={`badge estado ${ev._estado}`}>{ev._estado === 'finalizado' ? 'Finalizado' : 'Próximo'}</span>
                    </div>
                    <h3 className="title">{ev.titulo}</h3>
                    {(() => {
                      const { fecha, hora } = formatEventDateTime(ev.fechaISO || ev.fecha);
                      return (
                        <>
                          <p className="meta"><strong>📅 Fecha:</strong> {fecha}</p>
                          <p className="meta"><strong>⏰ Hora:</strong> {hora}</p>
                        </>
                      );
                    })()}
                    <p className="meta"><strong>{getTipoEmoji(ev.tipo)} Tipo:</strong> {ev.tipo}</p>
                    {ev.lugar && <p className="meta"><strong>📍 Lugar:</strong> {ev.lugar}</p>}
                    {typeof ev.precio !== 'undefined' && ev.precio !== null && (
                      <p className="price"><strong>Precio:</strong> <span className="price-amount">${Number(ev.precio).toFixed(2)} MXN</span></p>
                    )}
                    {ev.descripcion && <p className="desc">{ev.descripcion}</p>}
                    <div className="actions">
                      <button disabled className="cta-buy-btn">Finalizado</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        </>
      )}

      {/* Modal eliminado. Zoom inline dentro de la tarjeta */}
    </div>
  );
}


